import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { DeviceDetectorService } from 'ngx-device-detector';
import { UtilsService } from 'core/utils/utils.service';
import { FormBuilder } from '@angular/forms';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppStateService } from 'core/app-state/app-state.service';
import { AccountManager } from 'src/app/modules/shared/entities/account-manager.class';
import {
  ACCOUNT_DASHBOARD_ROUTES,
  ACCOUNT_DASHBOARD_SECTIONS,
  Touchpoint,
  TOUCHPOINT_NAVIGATION_PARAMS,
  TouchpointListPayload,
  TouchpointTemplateValues
} from '../../touchpoints/entities/touchpoints-entity';
import { TouchpointsService } from '../../touchpoints/services/touchpoints.service';
import { EditTouchpointComponent } from '../../touchpoints/edit-touchpoint/edit-touchpoint.component';
import { DashboardService } from '../services/dashboard.service';

@Component({
  selector: 'app-daily-touchpoints',
  templateUrl: './daily-touchpoints.component.html',
  styleUrls: ['./daily-touchpoints.component.scss'],
  standalone: false
})
export class DailyTouchpointsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() accountManagerEmployeeId: any;
  @Input() customerId: any;
  @Input() leadId: any;
  @Output() activeTab = new EventEmitter();

  loading = true;
  accountsBaseRoute: any;
  appStrings = this.appInfoService.appStrings;
  isAccountDashboard: boolean;
  isAdmin = this.appInfoService.isAdminTypeUser();

  touchpointList = this.touchpointsService.touchpointList;
  templateValues: TouchpointTemplateValues;
  touchpointTypesMobile = [];
  touchpointTypes = [];
  dateTimeFormat = this.appInfoService.dateTimeFormat;
  isOnline: boolean;
  selectedTypes = [1, 2, 3, 4];
  touchpointCount: string;
  touchpointTotal: string;
  title: string;
  isAccountsTouchpointsTab = false;

  data: any;

  notesOpen$ = new EventEmitter<boolean>();
  contactsOpen$ = new EventEmitter<boolean>();
  deleteOpen$ = new EventEmitter<boolean>();
  dialogButtons = [{ label: 'Close', type: 'secondary', onclick: () => { } }];
  dialogContent: string;

  displayedColumns: string[] = [];

  private subscriptions = new Subscription();
  private isInitialized = false;

  constructor(
    private touchpointsService: TouchpointsService,
    private appInfoService: AppInfoService,
    private appStateService: AppStateService,
    private ref: ChangeDetectorRef,
    private datePipe: DatePipe,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private utilsService: UtilsService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private deviceService: DeviceDetectorService,
    private dashboardService: DashboardService,
  ) { }

  filterForm = this.fb.group({
    selected: [this.selectedTypes],
  });
  dateRangeForm = this.fb.group({
    start: [null],
    end: [null],
  });

  ngOnInit() {
    this.isAccountDashboard = !!(this.customerId || this.leadId);
    this.checkRouteParameters();
    this.displayedColumns = [
      'type',
      'name',
      'subject',
      'contact',
      'time',
      'location',
      'notes',
      ...(this.canShowLastVisited ? ['lastVisited'] : []),
      ...(this.isAccountDashboard ? ['edit', 'delete'] : [])
    ];
    this.isOnline = this.appStateService.state.isOnline;
    this.accountsBaseRoute = this.customerId ? ACCOUNT_DASHBOARD_ROUTES.customer : ACCOUNT_DASHBOARD_ROUTES.lead;
    this.composeTitle();

    this.subscriptions.add(this.touchpointList.subscribe((value: Touchpoint[]) => {
      this.data = value.map(touchpoint => ({
        ...touchpoint,
        companyRedirect: this.getCompanyRedirect(touchpoint),
        showLastVisited: this.hasLastVisited(touchpoint)
      }));
    }));
    this.subscriptions.add(this.touchpointsService.touchpointCounter$.subscribe(value => {
      this.touchpointCount = value.count;
      this.touchpointTotal = value.total;
      this.addTitleCount();
    }));
    this.templateValues = this.touchpointsService.templateValues;
    this.touchpointList = this.touchpointsService.touchpointList;

    // Initialize with current week only for accounts touchpoints tab
    if (this.isAccountsTouchpointsTab) {
      this.initializeCurrentWeek();
    }

    if (this.templateValues) {
      this.initData();
    } else {
      this.touchpointsService.touchpointTemplateValues$.subscribe(value => {
        this.templateValues = value;
        this.initData();
      });
    }

    this.checkForOutlookTouchpoint();

    this.isInitialized = true;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges() {
    if (!this.isInitialized) {
      return;
    }
    this.loading = true;
    this.touchpointsService.getTouchpointList(this.getPayloadObject(), this.hideLoading.bind(this));
  }

  private checkRouteParameters(): void {
    const { params, url } = this.activatedRoute.snapshot;
    this.isAccountsTouchpointsTab = params['tab'] === 'touchpoints' && url.some(segment => segment.path === 'accounts'
      || segment.path === 'accounts-leads');
  }

  private initData() {
    this.loading = true;
    this.touchpointTypesMobile = this.utilsService.mapNumberKeyValueOptions(this.templateValues.TouchpointType);
    this.touchpointTypes = Object.keys(this.templateValues.TouchpointType).map(key => Number(key));
    setTimeout(() => {
      this.touchpointsService.getTouchpointList(this.getPayloadObject(), this.hideLoading.bind(this));
    });
  }

  private getPayloadObject() {
    let startDate: string | undefined;
    let endDate: string | undefined;

    if (this.isAccountsTouchpointsTab) {
      // Use form values for accounts touchpoints tab
      startDate = this.dateRangeForm.controls.start.value?.toISOString();
      endDate = this.dateRangeForm.controls.end.value?.toISOString();
    } else {
      // Use today's date for other views
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      startDate = today.toISOString();

      const endOfDay = new Date();
      endOfDay.setHours(23, 59, 59, 999);
      endDate = endOfDay.toISOString();
    }

    const payloadObject: TouchpointListPayload = {
      touchpointTypes: this.selectedTypes,
      date: {
        startDate,
        endDate
      },
    };
    if (this.accountManagerEmployeeId) {
      payloadObject.employeeId = this.accountManagerEmployeeId;
    }
    if (this.customerId) {
      payloadObject.customerId = this.customerId;
    }
    if (this.leadId) {
      payloadObject.leadId = this.leadId;
    }

    return payloadObject;
  }

  private composeTitle() {
    if (this.customerId || this.leadId) {
      this.title = this.appStrings.touchpoints_existingTouchpointsLabel.replace('({0})', this.touchpointTotal || '');
      return;
    }

    this.title = this.appStrings.label_touchpoints;
  }

  private addTitleCount() {
    if (this.customerId || this.leadId) {
      this.title = this.touchpointCount ? this.appStrings.touchpoints_existingTouchpointsLabel.replace('{0}', this.touchpointTotal || '')
        : this.appStrings.label_touchpointsNoTouchPointsForToday;
    }
  }

  showNoteDetails(touchpoint: Touchpoint) {
    this.dialogContent = '<xmp>' + touchpoint.notes + '</xmp>';
    this.ref.detectChanges();
    this.notesOpen$.emit(true);
  }

  showContactDetails(touchpoint: Touchpoint) {
    this.dialogContent = touchpoint.firstContactName;
    this.ref.detectChanges();
    this.contactsOpen$.emit(true);
  }

  getTimeTooltipText(touchpoint: Touchpoint) {
    const startDate =
      this.appStrings.label_start + ': ' + this.datePipe.transform(touchpoint.touchpointStartDateLabel, this.dateTimeFormat) + '\n';
    const endDate =
      this.appStrings.label_end + ': ' + this.datePipe.transform(touchpoint.touchpointEndDateLabel, this.dateTimeFormat) + '\n';
    const alarmType =
      this.appStrings.label_alarm + ': ' + this.templateValues.AlarmType[touchpoint.alarmType] + '\n';
    return startDate + endDate + alarmType;
  }

  handleSeeAll() {
    if (this.customerId || this.leadId) {
      this.activeTab.emit(2);
      return;
    }
    this.router.navigate(['/touchpoints']);
  }

  handleNewTouchpoint() {
    this.resolveAccountManagerId(accountManagerId => {
      const dialogRef = this.dialog.open(EditTouchpointComponent, {
        data: {
          customerId: this.customerId,
          leadId: this.leadId,
          accountManagerId,
          isAccountDashboard: this.isAccountDashboard,
          employeeId: this.adminSelectedEmployeeId,
        },
        panelClass: ['ds', 'fullWidth', this.deviceService.isMobile() ? 'fullScreen' : ''],
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.initData();
        }
      });
    });
  }

  hideLoading() {
    setTimeout(() => {
      this.loading = false;
    }, 100);
  }

  toggleCheckbox(checkedType: number) {
    this.loading = true;
    if (this.selectedTypes.includes(checkedType)) {
      this.selectedTypes = this.selectedTypes.filter((type) => type !== checkedType);
    } else {
      this.selectedTypes.push(checkedType);
    }
    this.touchpointsService.getTouchpointList(this.getPayloadObject(), this.hideLoading.bind(this));
  }

  handleMobileFiltering() {
    this.loading = true;
    this.selectedTypes = this.filterForm.controls.selected.value;
    this.touchpointsService.getTouchpointList(this.getPayloadObject(), this.hideLoading.bind(this));
  }

  /**
   * @param touchpoint - Selected Touchpoint
   * @returns customer or lead dashboard URL, or null when there is nothing to link to
   */
  getCompanyRedirect(touchpoint: Touchpoint): string | null {
    return this.isAccountDashboard ? null : this.touchpointsService.getAccountDashboardUrl(touchpoint);
  }

  /**
   * Opens the touchpoint's account dashboard scrolled to Account Notes
   * @param touchpoint - Selected Touchpoint
   */
  onLastVisited(touchpoint: Touchpoint) {
    this.router.navigate([this.touchpointsService.getAccountDashboardUrl(touchpoint)], {
      queryParams: { [TOUCHPOINT_NAVIGATION_PARAMS.section]: ACCOUNT_DASHBOARD_SECTIONS.notes }
    });
  }

  getContactButtonLabel(touchpoint: Touchpoint) {
    return this.appStrings.touchpoints_moreContacts.replace('{0}', touchpoint.moreContactsLength.toString());
  }

  /** Last Visited is offered on the main dashboard and the account Touchpoints tab, not the account Dashboard tab. */
  private get canShowLastVisited(): boolean {
    return !this.isAccountDashboard || this.isAccountsTouchpointsTab;
  }

  private hasLastVisited(touchpoint: Touchpoint): boolean {
    return this.canShowLastVisited && !touchpoint.isOutlookOnly && Boolean(this.touchpointsService.getAccountDashboardUrl(touchpoint));
  }

  private get adminSelectedEmployeeId(): string | undefined {
    return this.isAdmin ? (this.accountManagerEmployeeId ?? undefined) : undefined;
  }

  private resolveAccountManagerId(callback: (accountManagerId: string | null) => void) {
    if (!this.accountManagerEmployeeId) {
      callback(null);
      return;
    }

    this.dashboardService.getAccountManagers((list: AccountManager[]) => {
      callback(list.find(am => am.employeeId === this.accountManagerEmployeeId)?.id ?? null);
    });
  }

  handleEditTouchpoint(touchpoint: Touchpoint) {
    this.resolveAccountManagerId(accountManagerId => {
      const dialogRef = this.dialog.open(EditTouchpointComponent, {
        data: {
          customerId: touchpoint.customerId,
          leadId: touchpoint.leadId,
          touchpointId: touchpoint.id,
          accountManagerId: touchpoint.accountManagerId ?? accountManagerId,
          isAccountDashboard: this.isAccountDashboard,
          isOutlookOnly: touchpoint.isOutlookOnly === true,
          employeeId: this.adminSelectedEmployeeId,
        },
        panelClass: ['ds', 'fullWidth', this.deviceService.isMobile() ? 'fullScreen' : ''],
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.initData();
        }
      });
    });
  }

  /**
   * Delete selected touchpoint
   * @param touchpoint - Selected Touchpoint
   */
  deleteTouchpoint(touchpoint: Touchpoint) {
    const confirm = () => {
      this.loading = true;
      this.touchpointsService.deleteTouchpoint(touchpoint.id, false, null, () => {
        this.initData();
      }, touchpoint.isOutlookOnly === true, this.adminSelectedEmployeeId);
    };

    this.dialogButtons = [
      { label: 'Cancel', type: 'secondary', onclick: () => { } },
      { label: 'Delete', type: 'primary', onclick: confirm.bind(this) },
    ];

    this.ref.detectChanges();
    this.deleteOpen$.emit(true);
  }

  private initializeCurrentWeek(): void {
    const today = new Date();
    this.setWeekRange(today);
  }

  private setWeekRange(date: Date): void {
    if (!date) {
      this.dateRangeForm.controls.start.setValue(null);
      this.dateRangeForm.controls.end.setValue(null);
      return;
    }

    const selected = new Date(date);

    // Normalize time portion
    selected.setHours(0, 0, 0, 0);

    // Compute the week's Sunday (start) and the following Sunday (end, inclusive)
    const day = selected.getDay(); // 0 (Sun) .. 6 (Sat)
    const diffToSunday = -day; // move back to Sunday (0 => 0)
    const sundayStart = new Date(selected);
    sundayStart.setDate(selected.getDate() + diffToSunday);
    sundayStart.setHours(0, 0, 0, 0);

    const nextSundayEnd = new Date(sundayStart);
    nextSundayEnd.setDate(sundayStart.getDate() + 6);
    nextSundayEnd.setHours(23, 59, 59, 999);

    this.dateRangeForm.controls.start.setValue(sundayStart);
    this.dateRangeForm.controls.end.setValue(nextSundayEnd);
  }

  onDateSelected(startDate: Date) {
    this.setWeekRange(startDate);
    this.loading = true;
    this.touchpointsService.getTouchpointList(this.getPayloadObject(), this.hideLoading.bind(this));
  }

  private checkForOutlookTouchpoint(): void {
    const { params } = this.activatedRoute.snapshot;
    const entityId = params['entityId'];

    if (!entityId || params['tab'] !== 'touchpoints') {
      return;
    }

    setTimeout(() => {
      const dialogRef = this.dialog.open(EditTouchpointComponent, {
        data: {
          customerId: this.customerId,
          leadId: this.leadId,
          touchpointId: entityId,
          isMergingToCrm: params['isMergingToCrm'] === 'true',
          isAccountDashboard: this.isAccountDashboard,
          employeeId: this.adminSelectedEmployeeId,
        },
        panelClass: ['ds', 'fullWidth', this.deviceService.isMobile() ? 'fullScreen' : ''],
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.initData();
        }
        const baseRoute = this.customerId ? ACCOUNT_DASHBOARD_ROUTES.customer : ACCOUNT_DASHBOARD_ROUTES.lead;
        const accountId = this.customerId || this.leadId;
        this.router.navigate([baseRoute, accountId, { tab: 'touchpoints' }], { replaceUrl: true });
      });
    });
  }
}
